import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, ExternalLink } from "lucide-react";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">{label}</span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
    />
  </label>
);

const PostForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const heroRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugDirty, setSlugDirty] = useState(false);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    category: "",
    excerpt: "",
    body: "",
    hero_image_url: "",
    publish_date: "",
    is_published: true,
    sort_order: 0,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/posts");
        const found = data.find((p) => p.id === id);
        if (!found) {
          toast.error("Post not found");
          navigate("/admin");
          return;
        }
        // Trim publish_date to a value the datetime-local input accepts.
        const pd = found.publish_date ? found.publish_date.slice(0, 16) : "";
        setForm({ ...found, publish_date: pd });
        setSlugDirty(true);
      } catch {
        toast.error("Failed to load post");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: type === "checkbox" ? checked : (name === "sort_order" ? Number(value) || 0 : value) };
      // Auto-slug from title until the user manually edits the slug.
      if (name === "title" && !slugDirty) {
        next.slug = slugify(value);
      }
      if (name === "slug") {
        setSlugDirty(true);
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, hero_image_url: data.url }));
      toast.success("Hero image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (heroRef.current) heroRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        // datetime-local string → keep as-is; backend stores as ISO.
      };
      if (isEdit) {
        await api.put(`/admin/posts/${id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Post published");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="admin-post-form" className="min-h-screen bg-white pb-32">
      <header className="border-b border-black/10 px-6 md:px-12 py-6 sticky top-0 bg-white z-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] hover:opacity-60"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 pt-16">
        <p className="overline mb-4">{isEdit ? "Edit post" : "New post"}</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-16">
          {isEdit ? (
            <>Update <em className="italic font-light">article</em></>
          ) : (
            <>Write a <em className="italic font-light">new post</em></>
          )}
        </h1>

        <form onSubmit={handleSave} className="space-y-16">
          {/* Title + slug */}
          <section>
            <p className="overline mb-8">Basics</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Title *"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="The Mid-Summer Home Maintenance Checklist"
                testid="post-title"
                full
              />
              <Field
                label="Slug (URL)"
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="my-post-slug"
                testid="post-slug"
              />
              <Field
                label="Category"
                name="category"
                value={form.category}
                onChange={handleChange}
                placeholder="Home Maintenance"
                testid="post-category"
              />
            </div>
            {form.slug && (
              <p className="text-xs text-black/40 mt-4 flex items-center gap-2">
                Live URL:{" "}
                <a
                  href={`/blog/${form.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-black inline-flex items-center gap-1"
                  data-testid="post-slug-preview"
                >
                  /blog/{form.slug}
                  <ExternalLink size={10} />
                </a>
              </p>
            )}
          </section>

          {/* Hero image */}
          <section>
            <p className="overline mb-8">Hero image</p>
            <div className="flex flex-col sm:flex-row items-start gap-8">
              {form.hero_image_url ? (
                <div className="relative w-56 aspect-[4/3] bg-gray-100">
                  <img
                    src={resolveImage(form.hero_image_url)}
                    alt="Hero"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, hero_image_url: "" }))}
                    className="absolute -top-2 -right-2 bg-white border border-black/15 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                    data-testid="post-hero-remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-56 aspect-[4/3] bg-gray-100 flex items-center justify-center text-[10px] uppercase tracking-widest text-black/30">
                  No image
                </div>
              )}
              <div className="flex-1 w-full">
                <button
                  type="button"
                  onClick={() => heroRef.current?.click()}
                  disabled={uploading}
                  data-testid="post-hero-upload"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : form.hero_image_url ? "Replace image" : "Upload image"}
                </button>
                <p className="text-xs text-black/40 mt-3">
                  Landscape 4:3 or 16:9. JPG / PNG / WebP.
                </p>
                <div className="mt-6">
                  <Field
                    label="Or paste an image URL"
                    name="hero_image_url"
                    value={form.hero_image_url}
                    onChange={handleChange}
                    placeholder="https://…"
                    testid="post-hero-url"
                  />
                </div>
              </div>
            </div>
            <input ref={heroRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
          </section>

          {/* Excerpt */}
          <section>
            <p className="overline mb-8">Excerpt</p>
            <p className="text-sm text-black/60 mb-6 max-w-2xl">
              1–2 sentences. Shown on tile cards and search results.
            </p>
            <textarea
              data-testid="post-excerpt"
              name="excerpt"
              value={form.excerpt}
              onChange={handleChange}
              rows={3}
              className="w-full bg-transparent border border-black/15 px-4 py-3 text-base focus:outline-none focus:border-black transition-colors resize-y"
              placeholder="A quick mid-season check-up to keep your home cool, safe, and efficient…"
            />
          </section>

          {/* Body */}
          <section>
            <p className="overline mb-8">Body</p>
            <p className="text-sm text-black/60 mb-6 max-w-2xl">
              Write in <strong className="text-black">Markdown</strong>: use{" "}
              <code className="text-xs bg-black/5 px-1.5 py-0.5">**bold**</code>,{" "}
              <code className="text-xs bg-black/5 px-1.5 py-0.5">## Heading</code>,{" "}
              <code className="text-xs bg-black/5 px-1.5 py-0.5">- bullet</code>, and{" "}
              <code className="text-xs bg-black/5 px-1.5 py-0.5">[text](url)</code> for links.
            </p>
            <textarea
              data-testid="post-body"
              name="body"
              value={form.body}
              onChange={handleChange}
              rows={20}
              className="w-full bg-transparent border border-black/15 px-5 py-4 text-base focus:outline-none focus:border-black transition-colors font-mono leading-relaxed resize-y"
              placeholder="Write your post here…"
            />
          </section>

          {/* Publishing */}
          <section>
            <p className="overline mb-8">Publishing</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Publish date"
                name="publish_date"
                type="datetime-local"
                value={form.publish_date}
                onChange={handleChange}
                testid="post-publish-date"
              />
              <Field
                label="Sort priority (lower wins ties)"
                name="sort_order"
                type="number"
                value={form.sort_order}
                onChange={handleChange}
                testid="post-sort-order"
              />
              <label className="flex items-center gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_published"
                  checked={form.is_published}
                  onChange={handleChange}
                  data-testid="post-is-published"
                  className="w-5 h-5 accent-black"
                />
                <span className="text-sm">Published &nbsp;<span className="text-black/40">(unchecked = draft, hidden from site)</span></span>
              </label>
            </div>
          </section>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-black/10">
            <button
              type="submit"
              disabled={saving}
              data-testid="post-save"
              className="inline-flex items-center justify-center px-12 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Publish post"}
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-black border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
};

export default PostForm;
